import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import type { LinkAnalytics, QrSettings, ShortLink } from '@shared/types';
import { Spinner } from '../components/Spinner';
import { MetricCard } from '../components/Card';
import { Sparkline } from '../components/Sparkline';
import {
  IconCopy,
  IconExternal,
  IconQr,
  IconTrash,
} from '../components/Icons';
import { formatDate, formatNumber, formatRelative, shortUrlFor } from '../lib/format';
import { useToast } from '../lib/toast';

interface DetailResponse {
  link: ShortLink;
  qr: QrSettings | null;
}

export function LinkDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const [data, setData] = useState<DetailResponse | null>(null);
  const [analytics, setAnalytics] = useState<LinkAnalytics | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [savingQr, setSavingQr] = useState(false);
  const [qrPreset, setQrPreset] = useState<'small' | 'medium' | 'large'>('medium');
  const [qrForm, setQrForm] = useState({
    foregroundColor: '#0f172a',
    backgroundColor: '#ffffff',
    errorCorrectionLevel: 'M' as 'L' | 'M' | 'Q' | 'H',
    margin: 4,
  });

  useEffect(() => {
    if (!id) return;
    api
      .get<DetailResponse>(`/api/links/${id}`)
      .then((d) => {
        setData(d);
        if (d.qr) {
          setQrForm({
            foregroundColor: d.qr.foregroundColor,
            backgroundColor: d.qr.backgroundColor,
            errorCorrectionLevel: d.qr.errorCorrectionLevel,
            margin: d.qr.margin,
          });
        }
      })
      .catch((err) => setError(err instanceof Error ? err.message : String(err)));
    api
      .get<LinkAnalytics>(`/api/links/${id}/analytics?days=30`)
      .then(setAnalytics)
      .catch(() => {
        /* non-blocking */
      });
  }, [id]);

  if (error)
    return (
      <div className="rounded-lg bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-200 px-4 py-3 text-sm">
        {error}
      </div>
    );
  if (!data || !id)
    return (
      <div className="flex items-center justify-center py-24">
        <Spinner size={28} />
      </div>
    );

  const link = data.link;
  const target = shortUrlFor(link.code);
  const qrUrl = `/api/links/${link.id}/qr.svg`;
  const qrDownloadUrl = `${qrUrl}?preset=${qrPreset}`;

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Link
            to="/app/links"
            className="text-sm text-brand-600 dark:text-brand-300"
          >
            ← Back to links
          </Link>
          <h1 className="text-2xl font-bold tracking-tight mt-2">
            {link.title || link.code}
          </h1>
          <div className="mt-1 flex items-center gap-2 text-sm text-ink-500 dark:text-ink-400">
            <span className="font-mono text-xs px-2 py-0.5 rounded bg-ink-100 dark:bg-ink-800">
              {link.code}
            </span>
            <span>·</span>
            <span>{formatDate(link.createdAt)}</span>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            className="btn-secondary"
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(target);
                toast.success('Short URL copied');
              } catch {
                toast.error('Could not copy to clipboard');
              }
            }}
          >
            <IconCopy />
            Copy
          </button>
          <a
            className="btn-secondary"
            href={target}
            target="_blank"
            rel="noreferrer"
          >
            <IconExternal />
            Open
          </a>
          <button
            type="button"
            className="btn-danger"
            onClick={async () => {
              if (!confirm('Delete this short link? It can be restored later.')) return;
              try {
                await api.del(`/api/links/${link.id}`);
                toast.success('Link deleted');
                navigate('/app/links');
              } catch (err) {
                toast.error(err instanceof Error ? err.message : 'Failed');
              }
            }}
          >
            <IconTrash />
            Delete
          </button>
        </div>
      </header>

      <section className="grid gap-4 md:grid-cols-3">
        <MetricCard label="Total clicks" value={formatNumber(link.clickCount)} />
        <MetricCard label="Unique visitors" value={formatNumber(link.uniqueClickCount)} />
        {analytics && <MetricCard label="Qualified clicks" value={formatNumber(analytics.totals.qualified)} />}
        {analytics && <MetricCard label="Bot / scanner" value={formatNumber(analytics.totals.bot)} />}
        {analytics && <MetricCard label="Suspicious" value={formatNumber(analytics.totals.suspicious)} />}
        <MetricCard
          label="Status"
          value={<span className={link.status === 'active' ? 'badge-success' : 'badge-muted'}>{link.status}</span>}
        />
      </section>

      <section className="grid gap-6 lg:grid-cols-3">
        <div className="card p-5 lg:col-span-2 space-y-3">
          <h2 className="font-semibold">Destination</h2>
          <a
            className="block break-all rounded-lg bg-ink-50 dark:bg-ink-800 px-3 py-2 text-sm font-mono"
            href={link.destinationUrl}
            target="_blank"
            rel="noreferrer"
          >
            {link.destinationUrl}
          </a>
          <div className="grid sm:grid-cols-2 gap-3 text-sm">
            <Detail label="Short URL" value={target} mono />
            <Detail label="Redirect status" value={String(link.redirectStatus)} />
            <Detail
              label="Expires"
              value={link.expiresAt ? formatDate(link.expiresAt) : '—'}
            />
            <Detail
              label="Max clicks"
              value={link.maxClicks ? `${link.clickCount} / ${link.maxClicks}` : '—'}
            />
            <Detail label="Password" value={link.passwordEnabled ? 'Enabled' : '—'} />
            <Detail
              label="Updated"
              value={formatRelative(link.updatedAt)}
            />
          </div>
          {link.tags.length > 0 && (
            <div className="flex flex-wrap gap-1 pt-2">
              {link.tags.map((t) => (
                <span key={t} className="badge-info">
                  {t}
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="card p-5 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold flex items-center gap-2">
              <IconQr /> QR code
            </h2>
            <select
              className="input max-w-[140px] text-xs"
              value={qrPreset}
              onChange={(e) => setQrPreset(e.target.value as 'small' | 'medium' | 'large')}
              aria-label="QR print size"
            >
              <option value="small">Small label</option>
              <option value="medium">Handout</option>
              <option value="large">Poster</option>
            </select>
          </div>
          <div className="flex items-center justify-center bg-white rounded-lg p-4 border border-ink-200 dark:border-ink-700">
            <img src={qrUrl} alt="QR code" className="w-48 h-48" />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <a
              className="btn-secondary justify-center text-xs"
              href={qrDownloadUrl}
              download={`${link.code}-${qrPreset}.svg`}
            >
              SVG
            </a>
            <button
              type="button"
              className="btn-secondary justify-center text-xs"
              onClick={async () => {
                try {
                  await downloadQrPng(qrDownloadUrl, `${link.code}-${qrPreset}.png`, qrPreset);
                } catch (err) {
                  toast.error(err instanceof Error ? err.message : 'PNG export failed');
                }
              }}
            >
              PNG
            </button>
            <a
              className="btn-secondary justify-center text-xs"
              href={`/api/links/${link.id}/qr.pdf?preset=${qrPreset}`}
            >
              PDF
            </a>
          </div>
          <p className="text-xs text-ink-500 dark:text-ink-400">
            QR downloads include QR-specific UTM values so scans can be separated in analytics.
          </p>
          <div className="grid grid-cols-2 gap-2">
            <ColorInput
              label="Foreground"
              value={qrForm.foregroundColor}
              onChange={(v) => setQrForm({ ...qrForm, foregroundColor: v })}
            />
            <ColorInput
              label="Background"
              value={qrForm.backgroundColor}
              onChange={(v) => setQrForm({ ...qrForm, backgroundColor: v })}
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="label">Error correction</label>
              <select
                className="input"
                value={qrForm.errorCorrectionLevel}
                onChange={(e) =>
                  setQrForm({
                    ...qrForm,
                    errorCorrectionLevel: e.target.value as 'L' | 'M' | 'Q' | 'H',
                  })
                }
              >
                <option value="L">Low (L)</option>
                <option value="M">Medium (M)</option>
                <option value="Q">Quartile (Q)</option>
                <option value="H">High (H)</option>
              </select>
            </div>
            <div>
              <label className="label">Margin</label>
              <input
                type="number"
                className="input"
                min={0}
                max={16}
                value={qrForm.margin}
                onChange={(e) =>
                  setQrForm({ ...qrForm, margin: Math.max(0, Number(e.target.value)) })
                }
              />
            </div>
          </div>
          <button
            type="button"
            className="btn-primary w-full"
            disabled={savingQr}
            onClick={async () => {
              setSavingQr(true);
              try {
                await api.put(`/api/links/${link.id}/qr`, qrForm);
                toast.success('QR settings saved');
                // bust the SVG cache
                const img = document.querySelector('img[alt="QR code"]') as HTMLImageElement | null;
                if (img) img.src = `${qrUrl}?t=${Date.now()}`;
              } catch (err) {
                toast.error(err instanceof Error ? err.message : 'Save failed');
              } finally {
                setSavingQr(false);
              }
            }}
          >
            {savingQr && <Spinner size={16} />}
            Save QR settings
          </button>
        </div>
      </section>

      {analytics && (
        <section className="space-y-6">
          <div className="card p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold">Click activity (30 days)</h2>
              <a
                className="text-sm text-brand-600 dark:text-brand-300"
                href={`/api/links/${link.id}/events.csv`}
              >
                Export events CSV
              </a>
            </div>
            <Sparkline
              data={analytics.timeseries.map((d) => ({ date: d.date, total: d.totalClicks }))}
              height={140}
              showAxis
            />
          </div>

          <div className="grid gap-6 lg:grid-cols-3">
            <BreakdownCard title="Top countries" rows={analytics.topCountries} />
            <BreakdownCard title="Top states / regions" rows={analytics.topRegions} />
            <BreakdownCard title="Top cities" rows={analytics.topCities} />
            <BreakdownCard title="UTM sources" rows={analytics.topUtmSources} />
            <BreakdownCard title="UTM mediums" rows={analytics.topUtmMediums} />
            <BreakdownCard title="Bot classifications" rows={analytics.topBotClassifications} />
            <BreakdownCard title="Bot reasons" rows={analytics.topBotReasons} />
            <BreakdownCard title="Top referrers" rows={analytics.topReferrers} />
            <BreakdownCard title="Devices" rows={analytics.topDevices} />
            <BreakdownCard title="Browsers" rows={analytics.topBrowsers} />
          </div>

          <div className="card p-5">
            <div className="flex items-baseline justify-between mb-3">
              <h2 className="font-semibold">Recent events</h2>
              <p className="text-xs text-ink-500 dark:text-ink-400">
                IPs shown redacted to /24 (IPv4) or /48 (IPv6)
              </p>
            </div>
            {analytics.recent.length === 0 ? (
              <p className="text-sm text-ink-500 dark:text-ink-400">No clicks yet.</p>
            ) : (
              <div className="overflow-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>When</th>
                      <th>Type</th>
                      <th>Quality</th>
                      <th>Bot reason</th>
                      <th>Location</th>
                      <th>IP (redacted)</th>
                      <th>Network</th>
                      <th>Browser</th>
                      <th>OS</th>
                      <th>Device</th>
                      <th>Colo</th>
                      <th>Referrer</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analytics.recent.slice(0, 50).map((e) => (
                      <tr key={e.id}>
                        <td className="text-xs whitespace-nowrap" title={e.occurredAt}>
                          {formatRelative(e.occurredAt)}
                        </td>
                        <td>
                          <span
                            className={
                              e.eventType === 'success' ? 'badge-success' : 'badge-warn'
                            }
                          >
                            {e.eventType}
                          </span>
                        </td>
                        <td>
                          <span className={e.isQualifiedClick ? 'badge-success' : 'badge-warn'}>
                            {e.botClassification}
                          </span>
                        </td>
                        <td className="text-xs whitespace-nowrap">{e.botReason ?? '—'}</td>
                        <td className="text-sm">{formatLocation(e)}</td>
                        <td className="font-mono text-xs">{e.ipAddress ?? '—'}</td>
                        <td
                          className="text-xs truncate max-w-[160px]"
                          title={e.asOrganization ?? undefined}
                        >
                          {formatNetwork(e)}
                        </td>
                        <td>{e.browser ?? '—'}</td>
                        <td>{e.os ?? '—'}</td>
                        <td>{e.deviceType ?? '—'}</td>
                        <td className="text-xs">{e.colo ?? '—'}</td>
                        <td className="truncate max-w-[200px]">{e.referrer ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>
      )}
    </div>
  );
}

function formatLocation(e: {
  city: string | null;
  region: string | null;
  regionCode: string | null;
  country: string | null;
}): string {
  // Prefer "City, ST, US"; fall back gracefully when fields are missing.
  const region = e.regionCode || e.region;
  const parts = [e.city, region, e.country].filter((p): p is string => !!p && p.length > 0);
  return parts.length > 0 ? parts.join(', ') : '—';
}

function formatNetwork(e: { asn: number | null; asOrganization: string | null }): string {
  if (e.asOrganization) return e.asOrganization;
  if (e.asn != null) return `AS${e.asn}`;
  return '—';
}

async function downloadQrPng(
  svgUrl: string,
  filename: string,
  preset: 'small' | 'medium' | 'large',
) {
  const pixels = preset === 'small' ? 512 : preset === 'large' ? 2048 : 1024;
  const response = await fetch(svgUrl, { credentials: 'same-origin' });
  if (!response.ok) throw new Error('Could not load QR SVG.');
  const svg = await response.text();
  const blob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
  const objectUrl = URL.createObjectURL(blob);
  try {
    const img = await loadImage(objectUrl);
    const canvas = document.createElement('canvas');
    canvas.width = pixels;
    canvas.height = pixels;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas rendering is unavailable.');
    ctx.drawImage(img, 0, 0, pixels, pixels);
    const pngBlob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((out) => (out ? resolve(out) : reject(new Error('PNG export failed.'))), 'image/png');
    });
    const pngUrl = URL.createObjectURL(pngBlob);
    try {
      const a = document.createElement('a');
      a.href = pngUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } finally {
      URL.revokeObjectURL(pngUrl);
    }
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Could not render QR image.'));
    img.src = src;
  });
}

function Detail({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <div className="text-xs text-ink-500 dark:text-ink-400">{label}</div>
      <div className={`mt-1 ${mono ? 'font-mono text-xs break-all' : ''}`}>{value}</div>
    </div>
  );
}

function ColorInput({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div>
      <label className="label">{label}</label>
      <div className="flex gap-2">
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="h-9 w-12 rounded border border-ink-200 dark:border-ink-700 bg-transparent p-0"
        />
        <input
          type="text"
          className="input"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      </div>
    </div>
  );
}

function BreakdownCard({
  title,
  rows,
}: {
  title: string;
  rows: { key: string; count: number }[];
}) {
  if (!rows.length) {
    return (
      <div className="card p-5">
        <h3 className="font-semibold mb-2">{title}</h3>
        <p className="text-sm text-ink-500 dark:text-ink-400">No data yet.</p>
      </div>
    );
  }
  const max = Math.max(...rows.map((r) => r.count), 1);
  return (
    <div className="card p-5">
      <h3 className="font-semibold mb-3">{title}</h3>
      <ul className="space-y-2.5">
        {rows.map((r) => (
          <li key={r.key} className="text-sm">
            <div className="flex items-center justify-between gap-2 mb-1">
              <span className="font-medium truncate" title={r.key}>
                {r.key || '—'}
              </span>
              <span className="text-ink-500 dark:text-ink-400">
                {formatNumber(r.count)}
              </span>
            </div>
            <div className="h-1.5 rounded-full bg-ink-100 dark:bg-ink-800 overflow-hidden">
              <div
                className="h-full bg-brand-500"
                style={{ width: `${(r.count / max) * 100}%` }}
              />
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
